/**
 * Strings.
 *
 * English and Puerto Rican Spanish are both first-class from the first commit.
 * Retrofitting i18n means finding every string already inlined in a component,
 * and on this account that has meant a translation layer that only ever covered
 * the pages someone remembered.
 *
 * `es-PR`, not generic `es`: the wording is Puerto Rican, and the distinction
 * matters to the people reading it.
 */

export type Locale = 'en' | 'es-PR'

const STRINGS = {
  en: {
    'build.native': 'iOS',
    'build.web': 'Web',
    'build.unavailable': 'build unavailable',
    'build.unknown': 'environment unknown',
    'notify.failed': 'Notifications could not connect. Try again.',
    'notify.unsupported': 'Notifications are unavailable on this device.',
    'app.signIn': 'Sign in',
    'app.signOut': 'Sign out',
    'app.loading': 'Loading…',

    'auth.handle': 'Username',
    'auth.displayName': 'Your name',
    'auth.createAccount': 'Create account',
    'auth.usePasskey': 'Sign in with a passkey',
    'auth.noPasskeySupport': 'This browser cannot use passkeys. Try Chrome, Safari or Edge.',
    'auth.setupToken': 'Setup token',
    'auth.cancelled': 'That was cancelled. Nothing has changed.',

    /* The delegate door. Wording avoids the word "password" — there isn't one. */
    'auth.usePin': 'Use a PIN instead',
    'auth.backToPasskey': 'Back to passkey sign-in',
    'auth.pinTitle': 'Sign in with a PIN',
    'auth.pin': 'PIN',
    'auth.pinHint':
      'For accounts set up by someone else, and for app store reviewers. Five wrong PINs will lock the account until the person who made it unlocks it.',

    'account.passkeys': 'Passkeys',
    'account.addPasskey': 'Add a passkey',
    'account.removePasskey': 'Remove',
    'account.synced': 'Synced to your password manager',
    'account.deviceOnly': 'This device only',
    'account.neverUsed': 'Never used',
    'account.lastUsed': 'Last used',
    'account.recoveryWarning':
      'This account has one passkey and it is not synced. Lose that device and the account cannot be recovered — add a second passkey.',

    /*
     * Deleting the account. Both stores require this to be reachable from
     * inside the app, so these strings are not optional decoration.
     */
    'account.deleteTitle': 'Delete your account',
    'account.deleteExplain':
      'This removes your recipes, your saved links, your cooking history and your passkeys. It cannot be undone and there is no way to get any of it back.',
    'account.deleteStart': 'Delete my account',
    'account.deleteConfirmLabel': 'Type your username to confirm',
    'account.deleteConfirm': 'Permanently delete',
    'account.deleteCancel': 'Keep my account',
    'account.deleteDone': 'Your account and everything in it has been deleted.',

    'notify.enable': 'Turn on notifications',
    'notify.enabled': 'Notifications are on',
    'notify.blocked': 'Notifications are blocked in your browser settings.',
    'notify.inbox': 'Notifications',
    'notify.empty': 'Nothing yet.',

    'admin.title': 'Admin',
    'admin.users': 'Users',
    'admin.audit': 'Activity',
    'admin.errors': 'Errors',
    'admin.flags': 'Flags',
    'admin.settings': 'Settings',
    'admin.broadcast': 'Broadcast',
    'admin.devices': 'Devices',

    'env.beta': 'BETA — this is not the live app',
    'env.local': 'LOCAL',

    'error.generic': 'Something went wrong.',
    'error.offline': 'You appear to be offline.',

    'update.ready': 'A newer version is ready',
    'update.now': 'Update now',
    'update.refresh': 'Refresh app',
    'update.later': 'Later',

    'install.title': 'Put this on your home screen',
    'install.why': 'It gets its own icon and opens without the browser bar.',
    'install.ios': 'Tap Share, then “Add to Home Screen”.',
    'install.add': 'Add it',
    'install.no': 'No thanks',
  },

  'es-PR': {
    'build.native': 'iOS',
    'build.web': 'Web',
    'build.unavailable': 'compilación no disponible',
    'build.unknown': 'ambiente desconocido',
    'notify.failed': 'No se pudo conectar las notificaciones. Inténtalo de nuevo.',
    'notify.unsupported': 'Las notificaciones no están disponibles en este equipo.',
    'app.signIn': 'Entrar',
    'app.signOut': 'Salir',
    'app.loading': 'Cargando…',

    'auth.handle': 'Usuario',
    'auth.displayName': 'Tu nombre',
    'auth.createAccount': 'Crear cuenta',
    'auth.usePasskey': 'Entrar con passkey',
    'auth.noPasskeySupport': 'Este navegador no puede usar passkeys. Prueba Chrome, Safari o Edge.',
    'auth.setupToken': 'Código de configuración',
    'auth.cancelled': 'Se canceló. No cambió nada.',

    'auth.usePin': 'Usar un PIN',
    'auth.backToPasskey': 'Volver a entrar con passkey',
    'auth.pinTitle': 'Entrar con un PIN',
    'auth.pin': 'PIN',
    'auth.pinHint':
      'Para cuentas creadas por otra persona, y para quienes revisan la app en las tiendas. Cinco PIN incorrectos bloquean la cuenta hasta que la desbloquee quien la creó.',

    'account.passkeys': 'Passkeys',
    'account.addPasskey': 'Añadir un passkey',
    'account.removePasskey': 'Quitar',
    'account.synced': 'Sincronizado con tu gestor de contraseñas',
    'account.deviceOnly': 'Solo en este equipo',
    'account.neverUsed': 'Nunca se ha usado',
    'account.lastUsed': 'Último uso',
    'account.recoveryWarning':
      'Esta cuenta tiene un solo passkey y no está sincronizado. Si pierdes ese equipo, no se puede recuperar la cuenta — añade un segundo passkey.',

    'account.deleteTitle': 'Borrar tu cuenta',
    'account.deleteExplain':
      'Esto borra tus recetas, tus enlaces guardados, tu historial de cocina y tus passkeys. No se puede deshacer y no hay manera de recuperar nada.',
    'account.deleteStart': 'Borrar mi cuenta',
    'account.deleteConfirmLabel': 'Escribe tu nombre de usuario para confirmar',
    'account.deleteConfirm': 'Borrar permanentemente',
    'account.deleteCancel': 'Quedarme con mi cuenta',
    'account.deleteDone': 'Tu cuenta y todo lo que había en ella se ha borrado.',

    'notify.enable': 'Activar notificaciones',
    'notify.enabled': 'Las notificaciones están activadas',
    'notify.blocked': 'Las notificaciones están bloqueadas en tu navegador.',
    'notify.inbox': 'Notificaciones',
    'notify.empty': 'Nada todavía.',

    'admin.title': 'Administración',
    'admin.users': 'Usuarios',
    'admin.audit': 'Actividad',
    'admin.errors': 'Errores',
    'admin.flags': 'Flags',
    'admin.settings': 'Ajustes',
    'admin.broadcast': 'Mensaje',
    'admin.devices': 'Equipos',

    'env.beta': 'BETA — esta no es la app en vivo',
    'env.local': 'LOCAL',

    'error.generic': 'Algo salió mal.',
    'error.offline': 'Parece que no tienes conexión.',

    'update.ready': 'Hay una versión más nueva',
    'update.now': 'Actualizar',
    'update.refresh': 'Recargar app',
    'update.later': 'Ahora no',

    'install.title': 'Ponlo en tu pantalla de inicio',
    'install.why': 'Tiene su propio ícono y abre sin la barra del navegador.',
    'install.ios': 'Toca Compartir y luego “Añadir a inicio”.',
    'install.add': 'Añadir',
    'install.no': 'Ahora no',
  },
} as const

export type StringKey = keyof (typeof STRINGS)['en']

let locale: Locale = detect()

function detect(): Locale {
  const stored = localStorage.getItem('locale')
  if (stored === 'en' || stored === 'es-PR') return stored
  return navigator.language.toLowerCase().startsWith('es') ? 'es-PR' : 'en'
}

export function getLocale(): Locale {
  return locale
}

export function setLocale(next: Locale): void {
  locale = next
  localStorage.setItem('locale', next)
  document.documentElement.lang = next
}

/**
 * A missing translation falls back to English rather than rendering the key.
 * A user seeing `account.passkeys` learns nothing; a user seeing the English
 * learns what the control does.
 */
export function t(key: StringKey): string {
  return STRINGS[locale][key] ?? STRINGS.en[key] ?? key
}
